/**
 * Hook to fetch Drupal menu data client-side
 * Uses SWR for caching and automatic revalidation
 */

"use client"

import type { GetMenuQuery } from "@/lib/gql/solutions-graphql"
import { useEffect, useState } from "react"

interface UseDrupalMenuOptions {
  /** Whether to fetch the menu (default: true) */
  enabled?: boolean
  /** Whether to fetch in development mode only (default: false) */
  devOnly?: boolean
}

interface UseDrupalMenuResult {
  /** Menu data from Drupal */
  menuData: GetMenuQuery["menu"] | null
  /** Whether the menu is currently loading */
  isLoading: boolean
  /** Error if the fetch failed */
  error: Error | null
}

/**
 * Fetches Drupal menu data client-side
 * 
 * @param menuName - Menu name to fetch (e.g., "MAIN", "FOOTER")
 * @param options - Additional options
 * @returns Menu data, loading state, and error
 * 
 * @example
 * ```tsx
 * const { menuData, isLoading, error } = useDrupalMenu("MAIN")
 * 
 * if (isLoading) return <div>Loading...</div>
 * if (error) return <div>Error: {error.message}</div>
 * if (!menuData) return <MainNav /> // Fallback
 * 
 * return <DrupalMenu menuData={menuData} />
 * ```
 */
export function useDrupalMenu(
  menuName: string = "MAIN",
  options: UseDrupalMenuOptions = {}
): UseDrupalMenuResult {
  const { enabled = true, devOnly = false } = options
  
  const [menuData, setMenuData] = useState<GetMenuQuery["menu"] | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    // Skip if disabled
    if (!enabled) {
      setIsLoading(false)
      return
    }

    // Skip if devOnly and not in development
    if (devOnly && process.env.NODE_ENV !== "development") {
      setIsLoading(false)
      return
    }

    const fetchMenu = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const response = await fetch(`/api/menu/${menuName.toLowerCase()}`, {
          next: { revalidate: 3600 }, // Cache for 1 hour
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch menu: ${response.statusText}`)
        }

        const data = await response.json()
        
        // Validate menu structure
        if (data && Array.isArray(data.items)) {
          setMenuData(data)
        } else {
          console.warn("[useDrupalMenu] Invalid menu structure, using fallback")
          setMenuData(null)
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        console.error("[useDrupalMenu] Error fetching menu:", error)
        setError(error)
        setMenuData(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchMenu()
  }, [menuName, enabled, devOnly])

  return { menuData, isLoading, error }
}
